/**
 * A small RFC 4180 CSV reader, so an upload can be checked in the browser before
 * anything is sent.
 *
 * Handles what real exports contain: quoted fields, `""` escapes, commas and
 * newlines inside quotes, CRLF line ends, a UTF-8 byte-order mark, and
 * semicolon-separated files (Excel in many locales). It does not guess types —
 * every cell is a string; turning cells into dataset fields is the mapper's job.
 */

export interface ParsedCsv {
  header: string[];
  rows: string[][];
  delimiter: "," | ";";
}

/** Comma unless the header line has more semicolons than commas (outside quotes). */
export function detectDelimiter(text: string): "," | ";" {
  let commas = 0;
  let semicolons = 0;
  let quoted = false;
  for (const ch of text) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && (ch === "\n" || ch === "\r")) break;
    else if (!quoted && ch === ",") commas++;
    else if (!quoted && ch === ";") semicolons++;
  }
  return semicolons > commas ? ";" : ",";
}

/** Split CSV text into records of fields. Blank lines are dropped. */
export function parseRecords(text: string, delimiter: string = ","): string[][] {
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let quoted = false;
  let i = 0;

  const endField = () => {
    record.push(field);
    field = "";
  };
  const endRecord = () => {
    endField();
    // A line with a single empty field is a blank line, not a record.
    if (!(record.length === 1 && record[0] === "")) records.push(record);
    record = [];
  };

  while (i < source.length) {
    const ch = source[i]!;
    if (quoted) {
      if (ch === '"' && source[i + 1] === '"') {
        field += '"';
        i += 2;
        continue;
      }
      if (ch === '"') quoted = false;
      else field += ch;
      i++;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === delimiter) endField();
    else if (ch === "\r" && source[i + 1] === "\n") {
      endRecord();
      i++;
    } else if (ch === "\n" || ch === "\r") endRecord();
    else field += ch;
    i++;
  }
  if (field !== "" || record.length > 0) endRecord();
  return records;
}

/** Header plus rows, with every row padded or cut to the header's width. */
export function parseCsv(text: string): ParsedCsv {
  const delimiter = detectDelimiter(text);
  const [header = [], ...rest] = parseRecords(text, delimiter);
  const width = header.length;
  const rows = rest.map((r) =>
    r.length >= width ? r.slice(0, width) : [...r, ...Array<string>(width - r.length).fill("")],
  );
  return { header: header.map((h) => h.trim()), rows, delimiter };
}

/** Read a picked file as UTF-8 text. FileReader rather than `file.text()`, which
 *  some environments (jsdom among them) do not implement. */
export function readFileText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
    reader.readAsText(file, "utf-8");
  });
}
